import { Coffee, BookOpen, Briefcase, Video, Users, Timer, BookCheck, UsersRound, CheckSquare, Bell } from "lucide-react";

const modes = [
  {
    id: "chill",
    title: "Chill Mode",
    tagline: "Recharge & Connect",
    description: "Sometimes the best way to recharge your brain is to hang out with others in a low-pressure environment. Chill Mode lets you stream music, watch videos or chat while enjoying the virtual company of friends. Research on online communities notes that feelings of connection, belonging and shared values are essential for creating supportive learning environments. By offering a relaxed space to socialize, CSW helps combat loneliness and fosters the trust that makes later study sessions more productive.",
    icon: Coffee,
    gradient: "from-pink-500 via-rose-500 to-orange-400",
    bgGradient: "from-pink-500/10 via-rose-500/5 to-transparent",
    glowColor: "bg-pink-500/30",
    iconColor: "#ec4899",
    features: [
      { icon: Video, label: "Stream Together" },
      { icon: Users, label: "Virtual Hangouts" },
    ],
  },
  {
    id: "study",
    title: "Study Mode",
    tagline: "Focus & Achieve",
    description: "When it's time to hit the books, Study Mode provides structured tools and accountability features. Join focus rooms with Pomodoro timers, share your goals, and see your progress alongside others. Studies show that active participation in study groups leads to better outcomes – students who engage in study sessions are 30% more likely to succeed in their courses compared to those who study alone. The accountability of studying with peers is also powerful: research on goal completion found that simply committing to someone else increases the likelihood of following through from 25% to 65%, and scheduling a dedicated accountability appointment raises it to 95%. CSW's timers, goal-sharing boards and peer check-ins harness this effect so you start and finish tasks on time.",
    icon: BookOpen,
    gradient: "from-cyan-500 via-blue-500 to-indigo-500",
    bgGradient: "from-cyan-500/10 via-blue-500/5 to-transparent",
    glowColor: "bg-cyan-500/30",
    iconColor: "#06b6d4",
    features: [
      { icon: Timer, label: "Pomodoro Timer" },
      { icon: BookCheck, label: "Quizzes" },
      { icon: UsersRound, label: "Focus Groups" },
    ],
  },
  {
    id: "work",
    title: "Work Mode",
    tagline: "Plan & Execute",
    description: "For heavier projects or professional tasks, Work Mode combines task management with reminders and analytics. Create Kanban boards, set deadlines, and track your time to see where you're most productive. Gamified features like streaks and progress badges aren't just fun – a recent meta-analysis of 41 studies found that educational gamification yields a large positive effect size (Hedges' g = 0.822) on student learning outcomes. CSW's gamified tasks encourage consistency and offer small rewards to keep you motivated.",
    icon: Briefcase,
    gradient: "from-amber-500 via-orange-500 to-red-500",
    bgGradient: "from-amber-500/10 via-orange-500/5 to-transparent",
    glowColor: "bg-amber-500/30",
    iconColor: "#f59e0b",
    features: [
      { icon: CheckSquare, label: "Kanban Tasks" },
      { icon: Bell, label: "Smart Reminders" },
    ],
  },
];

export const Modes = () => {
  return (
    <section className="py-24 px-4 relative overflow-hidden" aria-labelledby="modes-title">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/30 to-background" aria-hidden="true" />
      
      <div className="container mx-auto relative z-10">
        <header className="text-center mb-20 animate-fade-in">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Three Powerful Modes
          </span>
          <h2 id="modes-title" className="font-display text-4xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
              Study Modes
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            CSW adapts to your workflow – whether you're unwinding, grinding, or executing
          </p>
        </header>

        <div className="space-y-8 max-w-6xl mx-auto">
          {modes.map((mode, index) => {
            const Icon = mode.icon;
            const isEven = index % 2 === 0;
            
            return (
              <article
                key={mode.id}
                className="group relative animate-fade-in"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                {/* Card */}
                <div className="relative rounded-3xl border border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden transition-all duration-500 hover:border-border hover:shadow-2xl">
                  {/* Gradient background on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${mode.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} aria-hidden="true" />
                  
                  {/* Glow effect */}
                  <div className={`absolute -top-20 ${isEven ? '-right-20' : '-left-20'} w-40 h-40 ${mode.glowColor} rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-500`} aria-hidden="true" />
                  
                  <div className={`relative p-8 md:p-10 flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}>
                    {/* Icon Section */}
                    <div className="flex-shrink-0">
                      <div className={`w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gradient-to-br ${mode.gradient} p-[2px] shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                        <div className="w-full h-full rounded-2xl bg-card flex items-center justify-center">
                          <Icon 
                            className="w-12 h-12 md:w-16 md:h-16" 
                            style={{ color: mode.iconColor }}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Content Section */}
                    <div className="flex-1 text-center md:text-left">
                      <div className="mb-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${mode.gradient} text-white mb-2`}>
                          {mode.tagline}
                        </span>
                        <h3 className={`text-3xl md:text-4xl font-bold bg-gradient-to-r ${mode.gradient} bg-clip-text text-transparent`}>
                          {mode.title}
                        </h3>
                      </div>
                      
                      <p className="text-muted-foreground leading-relaxed mb-6 text-sm md:text-base">
                        {mode.description}
                      </p>
                      
                      {/* Features */}
                      <div className="flex flex-wrap justify-center md:justify-start gap-3">
                        {mode.features.map((feature) => {
                          const FeatureIcon = feature.icon;
                          return (
                            <div 
                              key={feature.label}
                              className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 border border-border/50 hover:border-border transition-colors"
                            >
                              <FeatureIcon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                              <span className="text-sm font-medium text-foreground">{feature.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};